import { Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';

import { CrmPromotionProviderService, PromotionMap } from '../../../crm-promotion/crm-promotion-provider.service';
import { AntepostFixtureGroup, AntepostFixtureListModel } from './antepost-fixture-list.resolver';

interface AntepostFixtureViewModel extends WinGameFixture {
    url: string;
}

interface AntepostFixtureGroupViewModel extends AntepostFixtureGroup {
    groupName: string;
    fixtures: AntepostFixtureViewModel[];
}

interface AntepostFixtureListComponentViewModel {
    groups: AntepostFixtureGroupViewModel[];
}

@HooksWireup()
@Component({
    selector: 'ms-antepost-fixture-list',
    templateUrl: 'antepost-fixture-list.component.html',
    standalone: false,
})
export class AntepostFixtureListComponent extends OnRouteResolve<AntepostFixtureListModel> {
    @HostBinding('class') className = 'fixture-list fixture-antepost';

    expandedGroups = new WeakMap<AntepostFixtureGroupViewModel, boolean>();
    vm: AntepostFixtureListComponentViewModel;

    fixturePromos: StringDictionary<string> = {};
    meetingPromos: StringDictionary<number> = {};

    constructor(
        public sitecore: Sitecore,
        private urlHelper: UrlHelperService,
        route: ActivatedRoute,
        private crmPromotionProvider: CrmPromotionProviderService,
    ) {
        super(route);

        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((promoMap) => this.promotionsChanged(promoMap));
    }

    onRouteResolved(model: AntepostFixtureListModel): void {
        if (model && model.fixtureGroups && model.fixtureGroups.length) {
            this.buildViewModel(model);
            this.vm.groups.forEach((group) => {
                this.expandedGroups.set(group, !!(group.fixtures && group.fixtures.length > 0));
            });
        } else {
            this.vm = {
                groups: [],
            };
        }
    }

    private promotionsChanged = (promosMap: PromotionMap) => {
        this.fixturePromos = promosMap.fixtures;
        this.meetingPromos = promosMap.meetings;
    };

    private buildViewModel(model: AntepostFixtureListModel): void {
        this.vm = {
            ...model,
            groups: model.fixtureGroups.map((fg) => ({
                ...fg,
                groupName: translationToStr(fg.fixtureGroup.tag.name),
                fixtures: fg.fixtures.map((f) => ({
                    ...f,
                    url: this.urlHelper.getFixtureUrlByFixture(f),
                })),
            })),
        };
    }

    toggleGroupExpanded(group: AntepostFixtureGroupViewModel): void {
        this.expandedGroups.set(group, !this.expandedGroups.get(group));
    }
}
